import React, { useCallback, useEffect, useRef, useState } from 'react';
import { buttonClass } from '../components/ui/Button';
import Tag from '../components/ui/Tag';
import Alert from '../components/ui/Alert';
import EmptyState from '../components/ui/EmptyState';
import Reveal from '../components/ui/Reveal';
import { Appear } from '../components/ui/Appear';
import { Link } from 'react-router-dom';
import { apiFetchAuthed } from '../apiHelpers';

// Read-only identity/consistency report (GET /api/charge/inconsistencies).
// Surfaces chargers wearing the wrong VPN profile (device hostname != OpenVPN
// common_name), VPN IPs off the 10.8.5.{seq} convention, and online-but-untracked
// chargers. The detection + re-attribution WRITES happen server-side on the fleet
// poll; this panel only displays. See docs/features/smart-charger-platform.md.

interface Contender {
  device_hostname: string; common_name: string | null; last_seen: string; count: number;
}
interface WrongProfile {
  vpn_ip: string; common_name: string; device_hostname: string; online: boolean;
  geo_label: string | null;
  true_asset: { asset_id: string; display_code: string; last_seen_geo: string | null } | null;
  profile_asset: { asset_id: string; display_code: string } | null;
  contenders: Contender[];
}
interface VpnMismatch { asset_id: string; display_code: string; vpn_ip: string; expected_vpn_ip: string; }
interface Untracked { vpn_ip: string; device_hostname: string | null; common_name: string | null; geo_label: string | null; }
interface Report {
  collector_ok: boolean;
  generated_at: string | null;
  wrong_profile: WrongProfile[];
  vpn_mismatch: VpnMismatch[];
  untracked_online: Untracked[];
  counts: { wrong_profile: number; vpn_mismatch: number; untracked_online: number };
}

function total(r: Report | null): number {
  if (!r) return 0;
  return r.counts.wrong_profile + r.counts.vpn_mismatch + r.counts.untracked_online;
}

export default function ChargerInconsistencies() {
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = (await apiFetchAuthed('/charge/inconsistencies', { silent: true })) as Report;
      if (!mounted.current) return;
      setReport(r);
      setOpen(total(r) > 0);   // auto-expand only when something needs attention
    } catch (e: any) {
      if (mounted.current) setError(e?.message || 'Failed to load inconsistencies');
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const count = total(report);
  const clean = report != null && count === 0;

  return (
    <div className={`mb-4 rounded-md border ${count > 0 ? 'border-amber-600/50 bg-amber-500/5' : 'border-gray-700 bg-[#2a2a2a]'}`}>
      <div className="flex items-center justify-between gap-2 px-4 py-3">
        <button
          type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
          className="flex items-center gap-2 min-w-0 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
            className={`flex-shrink-0 text-on-surface-variant transition-transform ${open ? 'rotate-90' : ''}`}>
            <path d="M9 18l6-6-6-6" />
          </svg>
          <span className="font-semibold text-white truncate">Inconsistencies</span>
          <Appear show={count > 0} className="flex-shrink-0">
            <Tag tone="warn">{count}</Tag>
          </Appear>
          <Appear show={clean} className="flex-shrink-0 text-xs text-emerald-400">all clear</Appear>
        </button>
        <button
          type="button" onClick={load} disabled={loading}
          className={`${buttonClass('quiet', 'sm')} flex-shrink-0`}
        >{loading ? 'Checking…' : 'Refresh'}</button>
      </div>

      <Reveal show={open}>
        <div className="px-4 pb-4 space-y-4">
          {error && <Alert variant="text" tone="error">{error}</Alert>}
          {report && !report.collector_ok && (
            <Alert variant="text" tone="warning">Collector unreachable — showing DB-only checks; live identity checks are paused.</Alert>
          )}

          {/* Wrong VPN profile — the important one. */}
          {report && report.wrong_profile.length > 0 && (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-amber-300 mb-2">
                Wrong VPN profile
              </h3>
              <div className="space-y-2">
                {report.wrong_profile.map((w) => (
                  <div key={w.vpn_ip} className="text-sm bg-black/20 border border-gray-700 rounded p-3">
                    <div className="text-on-surface">
                      <span className="font-mono text-white">{w.device_hostname}</span> is running{' '}
                      <span className="font-mono text-white">{w.common_name}</span>'s profile at{' '}
                      <span className="font-mono">{w.vpn_ip}</span>
                      {w.geo_label && <span className="text-on-surface-variant"> · {w.geo_label}</span>}
                      {!w.online && <span className="text-on-surface-variant"> · offline</span>}
                    </div>
                    <div className="mt-1 text-xs text-on-surface-variant">
                      Live data at {w.vpn_ip} belongs to{' '}
                      {w.true_asset
                        ? <Link to={`/assets/${w.true_asset.asset_id}`} className="text-primary hover:underline font-mono">{w.true_asset.display_code}</Link>
                        : <span className="font-mono">{w.device_hostname}</span>}
                      {' '}— it's been flagged for a VPN-profile fix.
                      {(w.geo_label || (w.true_asset && w.true_asset.last_seen_geo)) && (
                        <span> Last seen at {w.geo_label || w.true_asset!.last_seen_geo}
                          {!w.online && !w.geo_label ? ' (last known)' : ''}.</span>
                      )}
                      {w.contenders.length > 1 && (
                        <span> {w.contenders.length} devices have worn this profile.</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* VPN IP doesn't match the 10.8.5.{sequence} convention. */}
          {report && report.vpn_mismatch.length > 0 && (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-on-surface-variant mb-2">
                VPN IP off convention
              </h3>
              <div className="space-y-1">
                {report.vpn_mismatch.map((v) => (
                  <div key={v.asset_id} className="text-sm text-on-surface">
                    <Link to={`/assets/${v.asset_id}`} className="text-primary hover:underline font-mono">{v.display_code}</Link>
                    {' '}has <span className="font-mono">{v.vpn_ip}</span>, expected <span className="font-mono">{v.expected_vpn_ip}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Online but no asset record. */}
          {report && report.untracked_online.length > 0 && (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-on-surface-variant mb-2">
                Online, untracked
              </h3>
              <div className="space-y-1">
                {report.untracked_online.map((u) => (
                  <div key={u.vpn_ip} className="text-sm text-on-surface">
                    <span className="font-mono">{u.device_hostname || u.vpn_ip}</span>
                    {' '}is online at <span className="font-mono">{u.vpn_ip}</span> but has no asset record
                    {u.geo_label && <span className="text-on-surface-variant"> · {u.geo_label}</span>}
                  </div>
                ))}
              </div>
            </section>
          )}

          {clean && (
            <EmptyState variant="line">No wrong-profile, VPN-convention, or untracked-charger issues detected.</EmptyState>
          )}
        </div>
      </Reveal>
    </div>
  );
}
